<template>
  <VTableRemote
    v-if="hasPermission('deviceAnalysisKeys.view')"
    iconHeader="add"
    :title="$t('deviceAnalysisKeys.pageName')"
    :items="displayedItems"
    isSearchable
    hideDefaultFooter
    :isCustomIcon="true"
    :itemActions="actions"
    :pagination="pagination"
    :isInfinitScroll="true"
    :showExpand="true"
    centerColumns="['action']"
    :sortableColumns="['key','unit', 'name']"
    :notSortableColumns="['action', 'deviceType']"
    :headers="tableHeaders"
    @iconAction="onClickAction"
    @sendQueryParams="onQueryParams"
  >
    <template #expanded-item=" {headers, item}">
      <td :colspan="headers.length">
        <deviceAnalysisKeyOptions
          :item="item"
          @refreshKeys="onRefreshKeys"
        />
      </td>
    </template>
    <template #withInfiniteScroll>
      <div v-intersect="endIntersect" />
    </template>
  </VTableRemote>
</template>
<script>
import Vue from 'vue';
import { isEmpty } from 'lodash';
import VTableRemote from '@/components/shared/content-views/VTableRemote';
import { mapGetters } from 'vuex';
import DeviceAnalysisKeyForm from './DeviceAnalysisKeyForm.vue';
import deviceAnalysisKeyOptions from './deviceAnalysisKeyOptions.vue';
import deviceAnalysisKeyOptionForm from './deviceAnalysisKeyOptionForm.vue';

export default ({
    components: {
        VTableRemote,
        deviceAnalysisKeyOptions,
    },
    data() {
        return {
            deviceKeys: [],
            queryParams: { page: 1, perpage: 25 },
            deviceAnalysisKeys: [],
            pagination: {},
        };
    },
    computed: {
        ...mapGetters([
            'hasPermission',
            'userLang',
            'teamMainLang',
        ]),
        tableHeaders() {
            return ['key', 'name', 'unit', 'deviceTypes',
            ].map(header => ({
                text: this.$t(`deviceAnalysisKeys.${header}`),
                value: header,
            }));
        },
        actions() {
            const actions = [];
            if (this.hasPermission('deviceAnalysisKeys.edit')) {
                actions.push({
                    name: this.$t('deviceAnalysisKeys.actions.addOption'),
                    icon: 'add',
                    mode: 'add',
                });
                actions.push({
                    name: this.$t('forms.labels.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }

            return actions;
        },
        displayedItems() {
            return this.deviceAnalysisKeys.map(item => ({
                ...item,
                deviceTypes: item.deviceTypes.length ? item.deviceTypes.map(dType => (dType.type)) : [],
                unit: item?.unit[this.userLang] || item?.unit[this.teamMainLang],
                name: item.name ? item?.name[this.userLang] || item?.name[this.teamMainLang] : '',
            }));
        },
    },
    async created() {
        await this.getDeviceAnalysisKeys();
    },
    methods: {
        async getDeviceAnalysisKeys() {
            if (isEmpty(this.pagination) || (this.pagination?.currentPage !== this.queryParams?.page)) {
                const { data } = await this.$rq.getDeviceAnalysisKeys(this.queryParams);
                this.deviceAnalysisKeys = [...this.deviceAnalysisKeys, ...data.deviceAnalysisKeys];
                this.pagination = data.pagination;
            }
        },
        // eslint-disable-next-line consistent-return
        onClickAction(item, action) {
            const originalItem = this.deviceAnalysisKeys.find(key => key.id === item.id);
            // Adding new Key
            if (item === 'add') {
                return this.$root.$emit('showModal', {
                    title: this.$t('deviceAnalysisKeys.actions.add'),
                    component: Vue.component('deviceAnalysisKeyForm', DeviceAnalysisKeyForm),
                    showLangs: true,
                    onConfirm: requestPayload => this.onAddKey(requestPayload),
                });
            }
            // Editing Key
            if (action?.mode === 'edit') {
                return this.$root.$emit('showModal', {
                    title: this.$t('deviceAnalysisKeys.actions.update'),
                    component: Vue.component('deviceAnalysisKeyForm', DeviceAnalysisKeyForm),
                    payload: {
                        item: originalItem,
                        isEdition: true,
                    },
                    showLangs: true,
                    onConfirm: requestPayload => this.onUpdateKey(originalItem, requestPayload),
                });
            }
            // Adding Options
            if (action?.mode === 'add') {
                return this.$root.$emit('showModal', {
                    title: this.$t('deviceAnalysisKeys.actions.addOption'),
                    component: Vue.component('deviceAnalysisKeyOptionForm', deviceAnalysisKeyOptionForm),
                    payload: {
                        item: originalItem,
                    },
                    showLangs: true,
                    onConfirm: requestPayload => this.onAddOption(originalItem, requestPayload),
                });
            }
        },

        async onUpdateKey(item, requestPayload) {
            if (isEmpty(requestPayload)) return;
            if (requestPayload.deviceTypes && requestPayload.deviceTypes.length) {
                const dTypeIds = [];
                requestPayload.deviceTypes.forEach(dType => {
                    if (typeof dType === 'object') {
                        const { id } = dType;
                        dTypeIds.push(id);
                    } else {
                        dTypeIds.push(dType);
                    }
                });
                // eslint-disable-next-line no-param-reassign
                requestPayload.deviceTypes = dTypeIds;
            }
            const { data: key } = await this.$rq.updateDeviceAnalysisKey(item.id, requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('deviceAnalysisKeys.actions.updated', { data: key.key }),
            });
            this.onQueryParams({});
        },

        async onQueryParams(params) {
            this.queryParams = {
                ...this.queryParams,
                ...params,
                page: 1,
            };
            this.pagination = {};
            this.deviceAnalysisKeys = [];
            await this.getDeviceAnalysisKeys();
        },

        async onAddKey(payload) {
            const cleanPayload = this.$utils.removeEmptyNullOrUndefined(payload, true);
            if (!cleanPayload) return;
            const { data: key } = await this.$rq.addDeviceAnalysisKey(cleanPayload);
            this.$toasted.global.toast_success({
                message: this.$t('deviceAnalysisKeys.actions.added', { data: key.key }),
            });
            this.onQueryParams({});
        },

        async onAddOption(item, option) {
            if (item.deviceTypes && item.deviceTypes.length) {
                const dTypeIds = [];
                item.deviceTypes.forEach(dType => {
                    if (typeof dType === 'object') {
                        const { _id: id } = dType;
                        dTypeIds.push(id);
                    } else {
                        dTypeIds.push(dType);
                    }
                });
                // eslint-disable-next-line no-param-reassign
                item.deviceTypes = dTypeIds;
            }
            if (isEmpty(option)) return;
            const preparedItem = { ...item };
            preparedItem.options.push(option);
            const { data: updatedKey } = await this.$rq.updateDeviceAnalysisKey(item.id, preparedItem);
            this.onRefreshKeys(updatedKey);
        },

        // updates the deviceAnalysisKeys array by replacing an existing key with a updated one.
        // it will refresh the page after Update/delete key options
        async onRefreshKeys(key) {
            const indexToInsert = this.deviceAnalysisKeys.findIndex(k => k.id === key.id);
            const newArray = this.deviceAnalysisKeys.filter(obj => obj.id !== key.id);
            newArray.splice(indexToInsert, 0, key);
            this.deviceAnalysisKeys = newArray;
        },

        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getDeviceAnalysisKeys();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
    },
});
</script>
